import { Checkbox } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import {
  type CompaniesFilterChange,
  Filter,
  FilterContainer,
  FilterHeader,
  FilterPrice,
  FilterTitle,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { FilterLoadingLayer } from './ChangeTransportFiltersLoading';
import type { ClickParams } from '../../search/Curiosity/Analytics/utils';
import { useTransportPageFilterTracking } from './hooks/useTransportPageFilterTracking';

type ChangeTransportCompaniesFilterProps = {
  companies?: CompanyFilter[];
  onChange: (filterChange: CompaniesFilterChange) => void;
};

export function getCompaniesFilterChangeData(
  isTrain: boolean,
  companies: CompanyFilter[],
  newFilterName: string,
  sendCuriosityClickEvent: (
    eventData: ClickParams,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void,
  sendStratrackFlightPageFilterEvent: (payload: StartrackFilterPayload) => void
): CompaniesFilterChange {
  const newFilter = companies.map((val: CompanyFilter) =>
    val.name === newFilterName ? { ...val, selected: !val.selected } : val
  );
  sendStratrackFlightPageFilterEvent({
    filterName: isTrain ? 'TRAIN_COMPANIES_FILTER' : 'AIRLINES_FILTER',
    value: newFilterName,
    deselectFlag: !!companies.find((val) => val.name === newFilterName)?.selected,
  });
  sendCuriosityClickEvent({
    event_name: `click_on_${isTrain ? 'train_company' : 'airline'}_filters`,
    event_context: 'change_transport_page',
    event_value: newFilterName,
  });
  return {
    newState: {
      ...(isTrain ? { companies: newFilter } : { airlines: newFilter }),
    },
    event: { type: 'FILTER', origin: 'AIRLINES_FILTER' },
  };
}

export default function ChangeTransportCompaniesFilter(props: ChangeTransportCompaniesFilterProps) {
  const { companies, onChange } = props;
  const { isTrain } = useUrlSearchParams();
  const { t } = useTranslation();
  const { sendCuriosityClickEvent } = useCuriosity();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  return companies && companies.length > 1 ? (
    <Filter>
      <FilterHeader>
        <FilterTitle>
          {isTrain
            ? t('vacation.trainavailability.filters.companies')
            : t('vacation.flightavailability.filters.airlines')}
        </FilterTitle>
      </FilterHeader>
      <FilterContainer
        css={`
          row-gap: 8px;
          position: relative;
        `}
      >
        {companies.map((company) => (
          <CompanyFilterCheck
            key={company.name}
            {...company}
            onCompanyFilterChange={(value) =>
              onChange(
                getCompaniesFilterChangeData(
                  isTrain,
                  companies,
                  value,
                  sendCuriosityClickEvent,
                  sendStratrackFlightPageFilterEvent
                )
              )
            }
          />
        ))}
        <FilterLoadingLayer origin="AIRLINES_FILTER" />
      </FilterContainer>
    </Filter>
  ) : null;
}

type CompanyFilterCheckProps = {
  onCompanyFilterChange: (name: string) => void;
} & CompanyFilter;

function CompanyFilterCheck(props: CompanyFilterCheckProps) {
  const { enabled, selected, name, minPrice, onCompanyFilterChange } = props;
  return (
    <div
      css={`
        display: flex;
        align-items: center;
        justify-content: space-between;
      `}
    >
      <div
        css={`
          display: flex;
          flex-direction: column;
        `}
      >
        <span>{name}</span>
        <FilterPrice price={enabled ? minPrice : 0} />
      </div>
      <Checkbox
        checkbox={{ id: name }}
        disabled={!enabled}
        isChecked={selected}
        onChange={() => onCompanyFilterChange(name)}
      />
    </div>
  );
}
